import React, { useEffect, useMemo, useState } from "react";
import { printingApi } from "../../lib/printing/printingApi";
import api from "../../lib/api";
import {
  DOC_TYPES, DOC_TYPE_LABELS, DOC_SETTING_FIELDS, DOC_SETTING_LABELS, TRIGGERS, TRIGGER_LABELS, ROLE_LABELS,
  tenantDefaultsPayload, catalogOverridePayload, previewEffective, isCopyCountValid, textProblem, operatorMessage,
  timezoneSample,
} from "../../lib/printing/printingModel";
import { SectionCard, Field, CheckField, inputCls, btnPrimary, btnSecondary, btnLink, SourceTag, Notice, EmptyState, Loading } from "./PrintingUi";

function displayValue(field, value, { printers, layouts }) {
  if (value === null || value === undefined || value === "") return "—";
  switch (field) {
    case "enabled": case "auto_print": return value ? "Yes" : "No";
    case "printer_role": return ROLE_LABELS[value] || value;
    case "printer_id": return printers.find((p) => p.id === value)?.name || value;
    case "layout_id": return layouts.find((l) => l.id === value)?.name || (String(value).startsWith("system:") ? "System default" : value);
    case "trigger_events": return (value || []).map((t) => TRIGGER_LABELS[t] || t).join(", ") || "none";
    default: return String(value);
  }
}

/** Editor for one document type in one scope (tenant or catalog). */
function DocumentSettingsEditor({ docType, scope, catalogId, effective, tenantEffective, printers, layoutsForDoc, isAdmin, onSaved, notify }) {
  const settings = useMemo(() => effective?.settings || {}, [effective]);
  const sources = useMemo(() => effective?.sources || {}, [effective]);
  const isCatalog = scope === "catalog";
  const initialFlags = useMemo(() => {
    const f = {};
    DOC_SETTING_FIELDS.forEach((k) => { f[k] = isCatalog ? sources[k] === "catalog" : true; });
    return f;
  }, [isCatalog, sources]);
  const [flags, setFlags] = useState(initialFlags);
  const [edited, setEdited] = useState(() => Object.fromEntries(DOC_SETTING_FIELDS.map((k) => [k, settings[k]])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  useEffect(() => {
    setFlags(initialFlags);
    setEdited(Object.fromEntries(DOC_SETTING_FIELDS.map((k) => [k, settings[k]])));
    setPreview(null);
  }, [docType, scope, catalogId, initialFlags, settings]);

  const setVal = (k, v) => setEdited((e) => ({ ...e, [k]: v }));
  const toggleFlag = (k) => setFlags((f) => ({ ...f, [k]: !f[k] }));
  const ctx = { printers, layouts: layoutsForDoc };
  const previewed = previewEffective(tenantEffective, flags, edited);
  const resettableNow = DOC_SETTING_FIELDS.filter((k) => sources[k] === "catalog" && !flags[k]);

  const validate = () => {
    if (!isCopyCountValid(Number(edited.copy_count))) return "Copies must be between 1 and 5.";
    const pf = textProblem(edited.promo_footer || "");
    if (pf) return `Promotional footer: ${pf}`;
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError(null); setBusy(true);
    try {
      const normalized = { ...edited, copy_count: Number(edited.copy_count), promo_footer: (edited.promo_footer || "").trim(), printer_id: edited.printer_id || null, layout_id: edited.layout_id || null };
      if (normalized.layout_id) {
        const chosen = layoutsForDoc.find((l) => l.id === normalized.layout_id);
        normalized.layout_version = chosen ? chosen.version : null;
      } else normalized.layout_version = null;
      if (isCatalog) {
        if (resettableNow.length) await printingApi.clearCatalogOverride(catalogId, docType, resettableNow);
        const body = catalogOverridePayload(settings, flags, normalized);
        if (Object.keys(body).length) await printingApi.putCatalogOverride(catalogId, docType, body);
        notify("success", `${DOC_TYPE_LABELS[docType]} override saved for this catalog.`);
      } else {
        const body = tenantDefaultsPayload(settings, normalized);
        if (Object.keys(body).length === 0) { notify("info", "Nothing changed."); setBusy(false); return; }
        await printingApi.putDocumentDefaults(docType, body);
        notify("success", `${DOC_TYPE_LABELS[docType]} tenant defaults saved.`);
      }
      onSaved();
    } catch (err) { setError(operatorMessage(err)); }
    finally { setBusy(false); }
  };

  const loadPreview = async () => {
    setPreviewBusy(true);
    try { setPreview(await printingApi.preview({ doc_type: docType, catalog_id: isCatalog ? catalogId : null })); }
    catch (err) { setError(operatorMessage(err)); }
    finally { setPreviewBusy(false); }
  };

  const fieldRow = (k, control) => {
    const overridden = flags[k];
    const source = isCatalog ? (overridden ? "catalog" : previewed.sources[k]) : sources[k];
    return (
      <div key={k} className="grid grid-cols-1 gap-2 rounded-md border border-stone-200 p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] md:items-start" data-testid={`setting-${docType}-${k}`}>
        <div>
          <div className="text-sm font-medium text-stone-900">{DOC_SETTING_LABELS[k]}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-stone-600">
            <SourceTag source={source} />
            {(!isCatalog || !overridden) && <span data-testid={`inherited-${docType}-${k}`}>Current: {displayValue(k, isCatalog && !overridden ? previewed.settings[k] : settings[k], ctx)}</span>}
          </div>
        </div>
        <div>{(!isCatalog || overridden) ? control : <span className="text-sm text-stone-500">Inherited</span>}</div>
        {isCatalog && isAdmin && (
          <div>
            {overridden
              ? <button type="button" className={btnLink} onClick={() => toggleFlag(k)} data-testid={`reset-${docType}-${k}`}>Reset to inherited</button>
              : <button type="button" className={btnLink} onClick={() => toggleFlag(k)} data-testid={`override-${docType}-${k}`}>Override for this catalog</button>}
          </div>
        )}
      </div>
    );
  };

  const ro = !isAdmin;
  return (
    <div className="space-y-3" data-testid={`doc-editor-${scope}-${docType}`}>
      {error && <Notice tone="danger" testId={`doc-editor-error-${docType}`}>{error}</Notice>}
      {docType === "packing_slip" && !settings.enabled && <Notice tone="info">Packing-slip printing stays off until you enable it here or in a catalog override.</Notice>}
      {docType === "packing_slip" && <p className="text-xs text-stone-600" data-testid="packing-auto-note">To print a packing slip automatically with every paid order, use "Enable automatic packing slips" on the Overview tab. It prints on the Payment paid / Cash received triggers selected here.</p>}
      {fieldRow("enabled", <CheckField label="Print this document" checked={Boolean(edited.enabled)} onChange={(v) => setVal("enabled", v)} disabled={ro} testId={`enabled-${docType}`} />)}
      {fieldRow("auto_print", <CheckField label="Print automatically when a trigger fires" hint="Off means the document is queued only by manual print." checked={Boolean(edited.auto_print)} onChange={(v) => setVal("auto_print", v)} disabled={ro} testId={`auto-${docType}`} />)}
      {fieldRow("printer_role", (
        <Field label="Printer role"><select className={inputCls} value={edited.printer_role || "general"} onChange={(e) => setVal("printer_role", e.target.value)} disabled={ro} data-testid={`role-${docType}`}>
          {Object.entries(ROLE_LABELS).map(([id, label]) => <option key={id} value={id}>{label}</option>)}
        </select></Field>
      ))}
      {fieldRow("printer_id", (
        <Field label="Specific printer (optional)" hint="Overrides the role for this document."><select className={inputCls} value={edited.printer_id || ""} onChange={(e) => setVal("printer_id", e.target.value || null)} disabled={ro} data-testid={`printer-${docType}`}>
          <option value="">Use the role</option>
          {printers.filter((p) => !p.deleted_at).map((p) => <option key={p.id} value={p.id}>{p.name}{p.enabled === false ? " (disabled)" : ""}</option>)}
        </select></Field>
      ))}
      {fieldRow("copy_count", (
        <Field label="Copies (1–5)" hint="Each copy is a separate physical printout."><input className={inputCls} value={edited.copy_count ?? 1} onChange={(e) => setVal("copy_count", e.target.value)} disabled={ro} inputMode="numeric" data-testid={`copies-${docType}`} /></Field>
      ))}
      {fieldRow("layout_id", (
        <Field label="Layout"><select className={inputCls} value={edited.layout_id || ""} onChange={(e) => setVal("layout_id", e.target.value || null)} disabled={ro} data-testid={`layout-${docType}`}>
          <option value="">Current layout (follows new versions)</option>
          {layoutsForDoc.map((l) => <option key={`${l.id}-${l.version}`} value={l.id}>{l.name} — v{l.version}{l.is_current ? " (current)" : ""}</option>)}
        </select></Field>
      ))}
      {fieldRow("promo_footer", (
        <Field label="Promotional footer" hint="Plain text, up to 160 characters."><input className={inputCls} value={edited.promo_footer || ""} onChange={(e) => setVal("promo_footer", e.target.value)} disabled={ro} data-testid={`promo-${docType}`} maxLength={160} /></Field>
      ))}
      {fieldRow("trigger_events", (
        <fieldset>
          <legend className="text-sm font-medium text-stone-800">Print when</legend>
          {TRIGGERS.filter((t) => !t.id.startsWith("manual")).map((t) => (
            <CheckField key={t.id} label={t.label} hint={t.note || undefined} disabled={ro} testId={`trigger-${docType}-${t.id}`}
              checked={(edited.trigger_events || []).includes(t.id)}
              onChange={(v) => setVal("trigger_events", v ? [...(edited.trigger_events || []), t.id] : (edited.trigger_events || []).filter((x) => x !== t.id))} />
          ))}
          <p className="text-xs text-stone-600" data-testid={`trigger-note-${docType}`}>These triggers drive automatic printing. A customer receipt prints automatically only when the server master switch is on and automatic receipts are enabled for this business on the Overview tab; those two controls are shown there.</p>
        </fieldset>
      ))}

      <div className="rounded-md border border-stone-200 bg-stone-50 p-3" data-testid={`effective-preview-${docType}`}>
        <div className="text-sm font-semibold text-stone-900">Effective configuration preview</div>
        <p className="text-xs text-stone-600">What this document will use after saving: {isCatalog ? "catalog override → tenant default → system default." : "tenant default → system default."}</p>
        <dl className="mt-2 grid grid-cols-1 gap-1 text-xs sm:grid-cols-2">
          {DOC_SETTING_FIELDS.map((k) => (
            <div key={k} className="flex flex-wrap items-center gap-2"><dt className="text-stone-600">{DOC_SETTING_LABELS[k]}:</dt>
              <dd className="flex flex-wrap items-center gap-2 font-medium"><span data-testid={`effective-${docType}-${k}`}>{displayValue(k, isCatalog ? previewed.settings[k] : { ...settings, ...edited }[k], ctx)}</span>
                <SourceTag source={isCatalog ? previewed.sources[k] : (edited[k] !== settings[k] ? "tenant" : sources[k])} /></dd></div>
          ))}
        </dl>
        <div className="mt-2 flex flex-wrap gap-2">
          <button type="button" className={btnSecondary} onClick={loadPreview} disabled={previewBusy} data-testid={`text-preview-btn-${docType}`}>{previewBusy ? "Loading…" : "Show printed text (saved configuration)"}</button>
          {isAdmin && <button type="button" className={btnPrimary} onClick={save} disabled={busy} data-testid={`save-${scope}-${docType}`}>{busy ? "Saving…" : isCatalog ? "Save catalog override" : "Save tenant defaults"}</button>}
        </div>
        {preview && (
          <pre tabIndex={0} aria-label="Printed text preview" className="mt-2 max-h-72 overflow-auto rounded border border-stone-300 bg-white p-2 font-mono text-xs" data-testid={`text-preview-${docType}`}>{preview.text_preview}</pre>
        )}
      </div>
    </div>
  );
}

/**
 * The zone printed on paper ("Placed 12:16 CDT").
 *
 * Sep 2026 — READ-ONLY. This card used to be a free-text IANA box
 * writing `printing_config.business.timezone`, a second tenant-editable
 * zone that could disagree with `companies.timezone` — the value
 * scheduling, the Kitchen Display, Retail Orders and coupon reminders
 * all use. A receipt printing a different hour than the ticket beside it
 * is the defect; there is now one authority, and this surface reports it
 * rather than competing with it.
 *
 * The stored printing value is NOT deleted: it remains readable for
 * audit and still serves as the transitional fallback for a tenant who
 * has not yet set the canonical field (see
 * `services/scheduling/timezones.py`'s documented precedence). It simply
 * can no longer be created or changed here — `printing/config.py`
 * rejects a non-empty write with `timezone_is_read_only`.
 */
function ReceiptTimezoneCard() {
  const [resolved, setResolved] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/company/scheduling-timezone")
      .then(({ data }) => { if (alive) setResolved(data || null); })
      .catch(() => { if (alive) setResolved(null); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  const zone = resolved?.timezone || "";
  const sample = zone ? timezoneSample(zone) : null;

  return (
    <SectionCard
      title="Receipt time zone"
      description="Times printed on receipts and kitchen tickets use your Business Profile time zone, with its abbreviation (for example 12:16 CDT)."
    >
      <div className="text-sm text-stone-700 space-y-1" data-testid="receipt-timezone">
        <div data-testid="receipt-timezone-saved">
          {!loaded
            ? "Loading…"
            : zone
              ? `Currently: ${zone}`
              : "Not set — receipts print UTC, labelled"}
        </div>
        {sample && (
          <div data-testid="receipt-timezone-preview">Right now in {zone}: {sample}</div>
        )}
        {loaded && resolved && !resolved.is_canonical && (
          <div className="text-amber-700" data-testid="receipt-timezone-needs-canonical">
            {resolved.is_configured
              ? "This is a legacy printing-only setting. Set your time zone in Business Profile to make it official."
              : "No time zone is configured. Set it in Business Profile so receipts and scheduling agree."}
          </div>
        )}
        <div className="text-stone-600" data-testid="receipt-timezone-managed-elsewhere">
          Managed in <a href="/dashboard/profile" className="underline font-medium">Dashboard → Business Profile</a>,
          so scheduling, the kitchen display, order timestamps and receipts all use the same zone.
        </div>
      </div>
    </SectionCard>
  );
}

export default function DocumentDefaultsPanel({ documents, printers, layouts, catalogs, isAdmin, onChanged, notify }) {
  const [scope, setScope] = useState("tenant");
  const [catalogId, setCatalogId] = useState(catalogs[0]?.id || "");
  const [docType, setDocType] = useState("customer_receipt");
  const [catalogData, setCatalogData] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [catalogError, setCatalogError] = useState(null);

  const loadCatalog = async (id) => {
    if (!id) return;
    setLoadingCatalog(true); setCatalogError(null);
    try { setCatalogData(await printingApi.catalogOverrides(id)); }
    catch (err) { setCatalogError(operatorMessage(err)); }
    finally { setLoadingCatalog(false); }
  };
  useEffect(() => {
    if (scope !== "catalog" || !catalogId) return undefined;
    let alive = true;
    setLoadingCatalog(true); setCatalogError(null);
    printingApi.catalogOverrides(catalogId)
      .then((d) => { if (alive) setCatalogData(d); })
      .catch((err) => { if (alive) setCatalogError(operatorMessage(err)); })
      .finally(() => { if (alive) setLoadingCatalog(false); });
    return () => { alive = false; };
  }, [scope, catalogId]);

  const effectiveFor = scope === "catalog" ? catalogData?.effective?.[docType] : documents?.effective?.[docType];
  const layoutsForDoc = (layouts?.layouts || []).filter((l) => l.doc_type === docType);

  return (
    <div className="space-y-4" data-testid="defaults-panel">
      <SectionCard title="Defaults and catalog overrides" description="Settings inherit in this order: catalog override → tenant default → safe system default. Override only the fields that differ for a catalog.">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label="Scope">
            <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value)} data-testid="defaults-scope">
              <option value="tenant">Tenant defaults (all catalogs)</option>
              <option value="catalog">Catalog override</option>
            </select>
          </Field>
          {scope === "catalog" && (
            <Field label="Catalog">
              <select className={inputCls} value={catalogId} onChange={(e) => setCatalogId(e.target.value)} data-testid="defaults-catalog">
                <option value="">Choose a catalog</option>
                {catalogs.map((c) => <option key={c.id} value={c.id}>{c.name || c.id}</option>)}
              </select>
            </Field>
          )}
          <Field label="Document type">
            <select className={inputCls} value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="defaults-doctype">
              {DOC_TYPES.map((d) => <option key={d} value={d}>{DOC_TYPE_LABELS[d]}</option>)}
            </select>
          </Field>
        </div>
        {!isAdmin && <Notice tone="info">You can view these settings. Only a business administrator can change them.</Notice>}
        {scope === "catalog" && catalogs.length === 0 && <EmptyState testId="defaults-no-catalogs">No catalogs found. Create a catalog first, then come back to override its printing settings.</EmptyState>}
        {scope === "catalog" && catalogError && <Notice tone="danger">{catalogError}</Notice>}
        {scope === "catalog" && loadingCatalog && <Loading label="Loading catalog overrides…" />}
        {effectiveFor && (scope === "tenant" || (!loadingCatalog && catalogData)) && (
          <DocumentSettingsEditor
            key={`${scope}-${catalogId}-${docType}`}
            docType={docType} scope={scope} catalogId={catalogId} effective={effectiveFor} tenantEffective={documents?.effective?.[docType]}
            printers={printers} layoutsForDoc={layoutsForDoc} isAdmin={isAdmin} notify={notify}
            onSaved={() => { onChanged(); if (scope === "catalog") loadCatalog(catalogId); }}
          />
        )}
      </SectionCard>
      <ReceiptTimezoneCard />
    </div>
  );
}
